import { useMutation } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldSpace from "components/FormFields/Space";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import { ExternalIntegration, IntegrationCustomConfigData } from "types/generated";
import { formatApiSecretFile } from "utils/apiSecret";
import { getDrawerFormFix } from "utils/css";
import { downloadFile } from "utils/file";

import { CREATE_EXTERNAL_INTEGRATION_WITH_SECRET } from "../../../ServiceNow/gql";

type CreateIntegrationDrawerFormProps = {
  integration?: ExternalIntegration;
  isLoginPolicyActive: boolean;
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
};

type FormValues = {
  space: string;
  name: string;
  description: string;
  type: string;
  customConfigData: IntegrationCustomConfigData;
};

const CreateIntegrationDrawerForm = createDrawer(
  ({ integration, isLoginPolicyActive, trackAnalytics }: CreateIntegrationDrawerFormProps) => {
    const drawer = useModal();
    const isEditMode = !!integration?.id;

    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const builderForm = useForm<FormValues>({
      defaultValues: {
        space: integration?.spaceDetails.name ?? "",
        name: integration?.name ?? "",
        description: integration?.description ?? "",
        type: "BACKSTAGE",
        customConfigData: {},
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty },
    } = builderForm;

    const [createIntegration, { loading: creatingIntegration }] = useMutation(
      CREATE_EXTERNAL_INTEGRATION_WITH_SECRET,
      {
        refetchQueries: ["GetAccountExternalIntegrations"],
      }
    );

    const handleCloseDrawer = () => {
      drawer.hide();
    };

    const onCreateSubmit = (formData: FormValues) => {
      createIntegration({
        variables: {
          input: {
            space: formData.space,
            name: formData.name,
            description: formData.description,
            type: formData.type,
            customConfigData: formData.customConfigData,
          },
        },
      })
        .then(({ data }) => {
          if (data?.externalIntegrationCreate?.id) {
            const { name } = data.externalIntegrationCreate;
            const { id, secret } = data.externalIntegrationCreate.apiKey;
            const secretFile = formatApiSecretFile(secret, id);

            if (!secretFile) {
              return reportError({
                message:
                  "Something went wrong while API config generation. Please contact us at support or try to generate API config again.",
              });
            }

            downloadFile(`backstage-api-key-${name}.config`, secretFile);
            reportSuccess({
              title: "Integration was successfully created",
              message: `Your credentials file is being downloaded for Key ID: ${id}`,
            });
            reset();
            handleCloseDrawer();
          }
        })
        .catch(onError);
    };

    const onSubmit = (formData: FormValues) => {
      onCreateSubmit(formData);
    };

    const onClose = () => {
      trackAnalytics("Close Integration Form", {
        type: "backstage",
        mode: isEditMode ? "edit" : "create",
        dirty: isDirty,
      });
    };

    return (
      <DrawerForm isDirty={isDirty} onClose={onClose}>
        <FormProvider {...builderForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle title={isEditMode ? "Edit integration" : "Create integration"} />
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody fullHeight>
              {isLoginPolicyActive && (
                <Banner variant="warning" title="User management is inactive">
                  Integration will create API key with account admin privileges.
                </Banner>
              )}
              <Box gap="x-large" direction="column" margin="large 0 0" fullHeight>
                <FormField label="Name" error={errors?.name?.message} noMargin>
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Name your integration"
                      error={!!errors?.name}
                      {...register("name", { required: "Name field is required." })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
                <FormFieldSpace noMargin />
                <FormField label="Description" isOptional noMargin>
                  {({ ariaInputProps }) => (
                    <Textarea
                      placeholder="Enter the integration description"
                      {...register("description")}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
                {!isEditMode && (
                  <Box margin="large 0 0 0">
                    <Banner variant="info">
                      Upon API key creation, a file will be generated with the API secret, encoded
                      using base64. Please save this file somewhere safe as it will never be
                      available again. You will have to provide this API secret when configuring the
                      Backstage plugins.
                      {/* TODO: pending valid docs link */}
                      {/* <Box margin="medium 0 0 0">
                        <ReadMoreDocsLink
                          docsUrl={getDocsUrl("")}
                        />
                      </Box> */}
                    </Banner>
                  </Box>
                )}
              </Box>

              <DrawerFooter>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={creatingIntegration}
                    loading={creatingIntegration}
                  >
                    {isEditMode ? "Save" : "Create"}
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showIntegrationDrawerForm = createDrawerTrigger(CreateIntegrationDrawerForm);
